import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { GOOGLE_FONT_CATALOG, getGoogleFont, googleFontStack } from '../../utilities/googleFonts.js';

const CATEGORIES = ['all', 'sans', 'serif', 'mono', 'display'];

const categoryLabel = (category) => ({
  all: 'All',
  sans: 'Sans serif',
  serif: 'Serif',
  mono: 'Monospace',
  display: 'Display',
}[category] || category);

const GoogleFontPicker = ({
  id,
  label,
  value = '',
  onChange,
  onPreview,
  compact = false,
  allowUnsupported = true,
}) => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const selectedFont = getGoogleFont(value);
  const unsupportedValue = value && !selectedFont ? String(value) : '';
  const visibleFonts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return GOOGLE_FONT_CATALOG.filter((font) => (
      (category === 'all' || font.category === category)
      && (!normalizedQuery || font.family.toLowerCase().includes(normalizedQuery))
    ));
  }, [category, query]);

  return (
    <div className={`google-font-picker ${compact ? 'is-compact' : ''}`}>
      {label ? <label className="form-label small fw-semibold" htmlFor={`${id}-search`}>{label}</label> : null}
      {unsupportedValue && allowUnsupported ? (
        <div className="alert alert-light border py-2 px-3 small mb-2">
          Existing font: <strong>{unsupportedValue}</strong>. It will be preserved until you choose another font.
        </div>
      ) : null}
      <div className="input-group input-group-sm mb-2">
        <span className="input-group-text"><i className="bi bi-search" aria-hidden="true"></i></span>
        <input
          id={`${id}-search`}
          className="form-control"
          type="search"
          value={query}
          placeholder="Find a font"
          onChange={(event) => setQuery(event.target.value)}
        />
        <button
          className="btn btn-outline-secondary"
          type="button"
          disabled={!value}
          onClick={() => onChange('')}
          title="Use the default font"
        >
          <i className="bi bi-x-lg" aria-hidden="true"></i>
        </button>
      </div>
      <div className="google-font-categories" role="group" aria-label="Font categories">
        {CATEGORIES.map((item) => (
          <button
            className={`btn btn-sm ${category === item ? 'btn-primary' : 'btn-outline-secondary'}`}
            type="button"
            onClick={() => setCategory(item)}
            key={item}
          >
            {categoryLabel(item)}
          </button>
        ))}
      </div>
      <div className="google-font-list" role="listbox" aria-label={label || 'Google Fonts'}>
        <button
          className={`google-font-choice ${!value ? 'is-selected' : ''}`}
          type="button"
          role="option"
          aria-selected={!value}
          onClick={() => onChange('')}
        >
          <span>System default</span><small>Fast and familiar</small>
        </button>
        {visibleFonts.map((font) => (
          <button
            className={`google-font-choice ${selectedFont?.family === font.family ? 'is-selected' : ''}`}
            type="button"
            role="option"
            aria-selected={selectedFont?.family === font.family}
            onMouseEnter={() => onPreview?.(font.family)}
            onFocus={() => onPreview?.(font.family)}
            onClick={() => onChange(googleFontStack(font))}
            key={font.family}
          >
            <span style={{ fontFamily: googleFontStack(font) }}>{font.family}</span>
            <small>{categoryLabel(font.category)}</small>
          </button>
        ))}
        {!visibleFonts.length ? <p className="text-muted small p-3 mb-0">No matching fonts.</p> : null}
      </div>
    </div>
  );
};

GoogleFontPicker.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  compact: PropTypes.bool,
  allowUnsupported: PropTypes.bool,
};

export default GoogleFontPicker;
